import type { PotmTileProps } from '@modules/projects-category/components/directory-grid-potm/DirectoryPotmTileDesktop';
import type { AdvertCreative, ProjectAd } from '~/modules/adverts/type';
import type { ProjectTileViewModel as AdvertCreativeProjectTileViewModel } from '@modules/products-category/util/advert-creative-project-tile-view';
import type { PaginationState } from '~/modules/pagination/type';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProjectTileViewModel as ProjectTileViewModelRocco } from '@rocco/ui/tile-project/view-models/project';

interface WithKey {
    key: string;
}

/**
 * Information to render a regular Project tile
 */
export interface ProjectTile extends WithKey {
    component: 'ProjectTile';
    data: {
        project: TileViewModel<ProjectTileViewModelRocco>;
        index: number;
    };
}

/**
 * Information to render a sponsored Project tile. This looks like a regular
 * Project tile, but it displays a "sponsored" project.
 */
export interface ProjectAdvertGridTile extends WithKey {
    component: 'ProjectAdvertTile';
    data: {
        project: AdvertCreativeProjectTileViewModel;
        index: number;
    };
}

/**
 * Information to render a banner advert. The banner breaks up the project grid
 * span the full width of the grid and clearly looks like an advertisement.
 */
export interface AdvertProjectPlacementGridTile extends WithKey {
    component: 'AdvertProjectPlacementTile';
    data: {
        ad: ProjectAd;
    };
}

/**
 * Information to render a Project of the Month vertical tile. This tile is
 * twice the height of a regular project tile and can contain a video.
 */
export interface DirectoryPotmDesktopGridTile extends WithKey {
    component: 'DirectoryPotmTileDesktop';
    data: {
        potmData: PotmTileProps;
    };
}

/**
 * Displays a vertical tile advertisement. It looks like a Project vertical
 * tile.
 */
export interface AdvertPotmDesktopGridTile extends WithKey {
    component: 'AdvertPotmTileDesktop';
    data: {
        advertCreative: AdvertCreative;
    };
}

/**
 * Displays a vertical tile advertisement. It looks like a Project vertical
 * tile.
 */
export interface AdvertPotmMobileGridTile extends WithKey {
    component: 'AdvertPotmTileMobile';
    data: {
        advertCreative: AdvertCreative;
    };
}

/**
 * Information to render a big banner advert at the top of the page
 */
export interface AdvertBannerGridTile extends WithKey {
    component: 'AdvertBannerTile';
    data: {
        advertCreative: AdvertCreative;
    };
}

/**
 * Information to render a banner to advertise AP Connect events.
 *
 * Not sure what that actually looks like
 */
interface APConnectBannerGridTile extends WithKey {
    component: 'APConnectBanner';
    data: {};
}

/**
 * Unions of all the possible items that can be rendered via a "Project of
 * the Month" grid.
 */
export type GridTile =
    | ProjectTile
    | ProjectAdvertGridTile
    | AdvertProjectPlacementGridTile
    | DirectoryPotmDesktopGridTile
    | AdvertBannerGridTile
    | APConnectBannerGridTile
    | AdvertPotmDesktopGridTile
    | AdvertPotmMobileGridTile;

/**
 * Assembles a grid of project tiles, advertisements, and Project of the Month (POTM) tiles.
 *
 * We don't actually render the grid tiles here. We just assemble them in an array that can
 * be looped through by the rendering method.
 *
 * The Grid supports multiple column layouts:
 * - Desktop: 3 or 4 columns (configurable via columnCount parameter)
 * - Mobile: Always 1 column
 *
 * POTM and vertical ad display is controlled by tile count thresholds to ensure balanced layouts:
 * - 3-column: Requires 10+ tiles for 1 POTM, 21+ for 2 POTMs
 * - 4-column: Requires 14+ tiles for 1 POTM, 26+ for 2 POTMs
 * - Minimum threshold of (4 × columnCount - 2) tiles required for any vertical tiles
 *
 * The vertical tiles display "Projects of the Month" (POTM). They can be
 * swapped with sponsored vertical placement tiles.
 *
 * 3-column layout example:
 * ┌─┐┌─┐┌─┐
 * └─┘└─┘│ │
 * ┌─┐┌─┐│ │
 * └─┘└─┘└─┘
 * ┌────────┐
 * │ banner │
 * └────────┘
 * ┌─┐┌─┐┌─┐
 * └─┘└─┘└─┘
 * ┌─┐┌─┐┌─┐
 * │ │└─┘└─┘
 * │ │┌─┐┌─┐
 * └─┘└─┘└─┘
 * ┌─┐┌─┐┌─┐
 * └─┘└─┘└─┘
 * ┌────────┐
 * │ banner │
 * └────────┘
 *
 * 4-column layout example:
 * ┌─┐┌─┐┌─┐┌─┐
 * └─┘└─┘└─┘│ │
 * ┌─┐┌─┐┌─┐│ │
 * └─┘└─┘└─┘└─┘
 * ┌──────────────┐
 * │    banner    │
 * └──────────────┘
 * ┌─┐┌─┐┌─┐┌─┐
 * └─┘└─┘└─┘└─┘
 *
 * @param showArchiproConnectBanner - Whether to show the Archipro Connect banner at the top of the grid
 * @param desktop - Whether viewing on desktop (mobile always uses 1 column regardless of columnCount)
 * @param ads - Full-width advertisement creatives displayed at the top of the page
 * @param verticalTileAds - Vertical tile advertisements that replace the regular vertical tile projects
 * @param sponsoredAds - Sponsored project advertisements displayed at the top of the grid. They look like
 * regular project tiles, but with the words "sponsored"
 * @param projectAds - Banner ads that will break up the grid at regular intervals
 * @param potmFeatureOn - Whether to display vertical tile projects of the month
 * @param projectTiles - Regular project tiles to display in the grid
 * @param gridPotms - Project of the Month tiles that will be shown in vertical tiles
 * @param appliedFilterCount - Number of active filters. When filters are applied, some adverts get suppressed
 * @param pagination - Current pagination state for calculating which POTMs to show
 * @param columnCount - Number of columns for desktop layout (3 or 4). Mobile always uses 1 column. Defaults to 3
 * @returns Array of grid tiles in display order
 */
export const buildProjectTileGrid = (props: {
    showArchiproConnectBanner: boolean;
    desktop: boolean;
    ads: AdvertCreative[];
    verticalTileAds: AdvertCreative[];
    sponsoredAds: AdvertCreativeProjectTileViewModel[];
    projectAds: ProjectAd[];
    potmFeatureOn: boolean;
    gridPotms: PotmTileProps[];
    appliedFilterCount: number;
    pagination: PaginationState;
    projectTiles: TileViewModel<ProjectTileViewModelRocco>[];
    columnCount?: number;
    featuredProjectIds?: number[];
}): GridTile[] => {
    const {
        showArchiproConnectBanner,
        desktop,
        ads,
        verticalTileAds,
        sponsoredAds,
        projectAds,
        potmFeatureOn,
        gridPotms,
        appliedFilterCount,
        pagination,
        projectTiles,
        columnCount = 3, // Default to 3 columns if not specified
        featuredProjectIds = [],
    } = props;

    // including verticalTileAds, other code change is required if MAX_VERTICAL_TILE_PER_PAGE > 4
    const MAX_VERTICAL_TILE_PER_PAGE = 4;
    const GRID_SIZE_PER_VERTICAL_TILE = columnCount === 1 ? 1 : 2;

    // First, we build the baseline array of project tiles
    const tiles: GridTile[] = projectTiles.map((project, index) => {
        return {
            component: 'ProjectTile',
            data: {
                project,
                index,
            },
            key: project.model.id.toString(),
        };
    });

    // If our project list is unfiltered and we have sponsored projects, we prepend
    // the sponsored project at the top of the list
    if (appliedFilterCount === 0 && sponsoredAds.length > 0) {
        sponsoredAds.forEach((project, index) => {
            if (!project.advertCreative) {
                return;
            }

            tiles.unshift({
                component: 'ProjectAdvertTile',
                data: {
                    project,
                    index,
                },
                key: `sponsored_${project.tile.projectLink}`,
            });
        });
    }

    // If featured projects exist, move them to the front of the list.
    // Process in reverse order so the first featured ID ends up at index 0.
    // This runs AFTER sponsored ads so featured tiles always appear first.
    if (featuredProjectIds.length > 0) {
        for (let i = featuredProjectIds.length - 1; i >= 0; i--) {
            const id = featuredProjectIds[i];
            const featuredIndex = tiles.findIndex(
                (tile) =>
                    tile.component === 'ProjectTile' &&
                    (tile as ProjectTile).data.project.model.id === id,
            );
            if (featuredIndex > 0) {
                const featuredTile = tiles.splice(featuredIndex, 1)[0];
                if (featuredTile) {
                    tiles.unshift(featuredTile);
                }
            }
        }
    }

    // Create separate arrays for POTMs and vertical ad tiles, but only on desktop.
    // These will be combined later to create the vertical tiles that appear in the grid.
    // gridHeroTiles contains regular POTMs
    // gridAdvertTiles contains sponsored vertical tile ads
    const gridHeroTiles: GridTile[] = [];
    const gridAdvertTiles: GridTile[] = [];
    if (desktop) {
        verticalTileAds.forEach((advert) => {
            if (!advert) {
                return;
            }
            gridAdvertTiles.push({
                component: 'AdvertPotmTileDesktop',
                data: { advertCreative: advert },
                key: `advert-${advert.id}`,
            });
        });
        gridPotms.forEach((gridPtom) => {
            gridHeroTiles.push({
                component: 'DirectoryPotmTileDesktop',
                data: { potmData: gridPtom },
                key: `potm-${gridPtom.id}`,
            });
        });
    } else {
        verticalTileAds.forEach((advert) => {
            if (advert.video?.coverMobile || advert.video?.mp4Mobile) {
                gridAdvertTiles.push({
                    component: 'AdvertPotmTileMobile',
                    data: { advertCreative: advert },
                    key: `advert-${advert.id}`,
                });
            }
        });
    }

    // We only show the Project of the month vertical tiles if
    // - the POTM feature is enabled
    // - we are on desktop
    // - there are more than 2 POTMs
    const gridPotmsEnabled =
        potmFeatureOn && desktop && gridHeroTiles.length > 2;

    let paginatedVerticalTiles: GridTile[] = [];
    let verticalTileAdsCount = 0;
    // Only show vertical tile ads if we have enough regular tiles to create a balanced layout
    // Minimum threshold: 4 * columnCount - 2 tiles ensures sufficient content around the vertical ad
    if (gridAdvertTiles[0] && tiles.length >= 2 * columnCount - 2) {
        paginatedVerticalTiles.push(gridAdvertTiles[0]);
        verticalTileAdsCount = 1;
    }
    if (gridPotmsEnabled) {
        // Calculate POTMs per page, accounting for vertical ad tiles
        // If we have a vertical ad, we show 2 POTMs instead of 3
        const maxPotmCount = MAX_VERTICAL_TILE_PER_PAGE - verticalTileAdsCount;

        // Calculate starting position for POTMs on current page
        // e.g., page 1: offset = 0, page 2: offset = 2, page 3: offset = 4
        const potmOffset = (pagination.page - 1) * maxPotmCount;

        // Get POTMs for current page, limited by potmLimit
        paginatedVerticalTiles = paginatedVerticalTiles.concat(
            gridHeroTiles.slice(potmOffset, potmOffset + maxPotmCount),
        );
    }

    const flattenedTiles: GridTile[] = [];
    let verticalTilesIndex = 0;
    let adsIndex = 0;
    tiles.forEach((tile, index) => {
        const gridIndexWithoutAd =
            index + verticalTilesIndex * GRID_SIZE_PER_VERTICAL_TILE;
        const gridIndex = gridIndexWithoutAd + adsIndex * columnCount;

        // Add an ad tile if we need one
        if (projectAds[adsIndex] && needAdTile(columnCount, gridIndex)) {
            const adData = projectAds[adsIndex++] as ProjectAd;
            const adTile: GridTile = {
                component: 'AdvertProjectPlacementTile',
                data: {
                    ad: adData,
                },
                key: `project-placement-${adData.id}`,
            };
            flattenedTiles.push(adTile);
        }

        // Add a vertical tile if we need one
        const remainingTilesCount = tiles.length - index;
        if (
            paginatedVerticalTiles[verticalTilesIndex] &&
            needVerticalTile(
                columnCount,
                gridIndexWithoutAd,
                remainingTilesCount,
            )
        ) {
            const verticalTile = paginatedVerticalTiles[
                verticalTilesIndex++
            ] as GridTile;
            flattenedTiles.push(verticalTile);
        }

        flattenedTiles.push(tile);
    });

    // Prepend a big full width banner advert to the list if we have one.
    if (appliedFilterCount === 0 && ads.length && ads[0]) {
        flattenedTiles.unshift({
            component: 'AdvertBannerTile',
            data: {
                advertCreative: ads[0],
            },
            key: 'AdvertBannerTile',
        });
    }

    // Prepend an AP Connect banner advert to the list if needed
    if (showArchiproConnectBanner) {
        flattenedTiles.unshift({
            component: 'APConnectBanner',
            data: {},
            key: 'APConnectBanner',
        });
    }

    return flattenedTiles;
};

const needAdTile = (columnCount: number, gridIndex: number): boolean => {
    if (columnCount === 1) {
        return gridIndex === 9 || gridIndex === 18 || gridIndex === 31;
    }
    if (gridIndex % columnCount !== 0) {
        return false;
    }
    const row = Math.floor(gridIndex / columnCount);
    const everyNRow = columnCount === 4 ? 4 : 5;
    return (row - 2) % everyNRow === 0;
};

const needVerticalTile = (
    columnCount: number,
    gridWithoutAd: number,
    remainingTilesCount: number,
): boolean => {
    if (columnCount === 1) {
        return gridWithoutAd === 6;
    }
    if (columnCount === 4) {
        if (gridWithoutAd === 3 && remainingTilesCount >= 3) {
            return true;
        }
        if (gridWithoutAd === 12 && remainingTilesCount >= 4) {
            return true;
        }
        if (gridWithoutAd === 27 && remainingTilesCount >= 3) {
            return true;
        }
        if (gridWithoutAd === 36 && remainingTilesCount >= 4) {
            return true;
        }
    }
    if (columnCount === 3) {
        if (gridWithoutAd === 2 && remainingTilesCount >= 2) {
            return true;
        }
        if (gridWithoutAd === 9 && remainingTilesCount >= 3) {
            return true;
        }
        if (gridWithoutAd === 23 && remainingTilesCount >= 2) {
            return true;
        }
        if (gridWithoutAd === 33 && remainingTilesCount >= 3) {
            return true;
        }
    }
    return false;
};
